import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import pmvFieldOpsService, { type PmvDailyLogSheet } from '@/services/pmv-field-ops.service';
import { useProjects } from '@/hooks/useProjects';
import { buildProjectIdToLabelMap, resolveProjectDisplayFromMap } from '@/utils/pmvDisplay';

const statusColor: Record<string, string> = {
  DRAFT: 'default',
  IN_PROGRESS: 'processing',
  SUBMITTED: 'blue',
  VERIFIED: 'success',
  RETURNED: 'warning',
  CLOSED: 'default',
  CANCELLED: 'error',
};

const PmvDailyLogSheetListPage: React.FC = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: projects = [] } = useProjects();
  const projectLabels = useMemo(() => buildProjectIdToLabelMap(projects), [projects]);
  const [status, setStatus] = useState<string | undefined>();
  const [operatingDate, setOperatingDate] = useState<string | undefined>();
  const [createOpen, setCreateOpen] = useState(false);
  const [form] = Form.useForm();

  const listQ = useQuery({
    queryKey: ['pmv-daily-log-sheets', status, operatingDate],
    queryFn: () =>
      pmvFieldOpsService.listSheets({
        page: 1,
        pageSize: 100,
        ...(status ? { status } : {}),
        ...(operatingDate ? { operating_date: operatingDate } : {}),
      }),
  });

  const createMut = useMutation({
    mutationFn: (body: {
      project_id: string;
      operating_date: string;
      shift_code?: string;
      site_reference?: string;
      load_expected?: boolean;
    }) => pmvFieldOpsService.createSheet(body),
    onSuccess: (sheet) => {
      message.success(`Sheet ${sheet.sheet_number} created`);
      setCreateOpen(false);
      form.resetFields();
      qc.invalidateQueries({ queryKey: ['pmv-daily-log-sheets'] });
      navigate(`/asset/pmv/daily-log-sheets/${sheet.id}`);
    },
    onError: (e: Error) => message.error(e.message || 'Create failed'),
  });

  const rows = listQ.data?.data ?? [];

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Daily Log Sheets"
          subtitle="Bulk field capture envelope — individual daily logs remain operational authority"
          primaryActions={
            <Space wrap>
              <Button type="primary" onClick={() => setCreateOpen(true)}>
                New sheet
              </Button>
              <Button onClick={() => navigate('/asset/pmv/daily-logs')}>Daily Logs</Button>
              <Button onClick={() => navigate('/asset/pmv/daily-log-sheets/exceptions')}>
                Exceptions
              </Button>
            </Space>
          }
          extra={
            <Space wrap>
              <Select
                allowClear
                placeholder="Status"
                style={{ width: 150 }}
                value={status}
                onChange={setStatus}
                options={[
                  'DRAFT',
                  'IN_PROGRESS',
                  'SUBMITTED',
                  'VERIFIED',
                  'RETURNED',
                  'CLOSED',
                  'CANCELLED',
                ].map((v) => ({ value: v, label: v }))}
              />
              <DatePicker
                allowClear
                onChange={(d) => setOperatingDate(d ? d.format('YYYY-MM-DD') : undefined)}
              />
            </Space>
          }
        />
      }
    >
      <Table
        rowKey="id"
        size="small"
        loading={listQ.isLoading}
        dataSource={rows}
        pagination={{ pageSize: 25 }}
        scroll={{ x: 1100 }}
        onRow={(r) => ({
          onDoubleClick: () => navigate(`/asset/pmv/daily-log-sheets/${r.id}`),
          style: { cursor: 'pointer' },
        })}
        columns={[
          {
            title: 'Sheet',
            dataIndex: 'sheet_number',
            width: 140,
            render: (v: string, r: PmvDailyLogSheet) => (
              <Button type="link" onClick={() => navigate(`/asset/pmv/daily-log-sheets/${r.id}`)}>
                {v}
              </Button>
            ),
          },
          {
            title: 'Project',
            dataIndex: 'project_id',
            render: (id: string) => resolveProjectDisplayFromMap(projectLabels, id),
          },
          { title: 'Site', dataIndex: 'site_reference', width: 140, render: (v) => v || '—' },
          { title: 'Date', dataIndex: 'operating_date', width: 120 },
          { title: 'Shift', dataIndex: 'shift_code', width: 90 },
          {
            title: 'Status',
            dataIndex: 'status',
            width: 120,
            render: (s: string) => <Tag color={statusColor[s] || 'default'}>{s}</Tag>,
          },
          { title: 'Entries', dataIndex: 'entry_count', width: 90 },
          { title: 'Rev', dataIndex: 'row_version', width: 70 },
        ]}
      />

      <Modal
        title="New Daily Log Sheet"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={createMut.isPending}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            operating_date: dayjs(),
            shift_code: 'DAY',
            load_expected: true,
          }}
          onFinish={(v) =>
            createMut.mutate({
              project_id: v.project_id,
              operating_date: v.operating_date.format('YYYY-MM-DD'),
              shift_code: v.shift_code,
              site_reference: v.site_reference || null,
              load_expected: !!v.load_expected,
            })
          }
        >
          <Form.Item name="project_id" label="Project" rules={[{ required: true }]}>
            <Select
              showSearch
              optionFilterProp="label"
              options={projects.map((p: { id: string; project_code?: string; name?: string }) => ({
                value: p.id,
                label: `${p.project_code || ''} ${p.name || ''}`.trim() || p.id,
              }))}
            />
          </Form.Item>
          <Form.Item name="operating_date" label="Operating date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="shift_code" label="Shift">
            <Select options={['DAY', 'NIGHT', 'GENERAL'].map((v) => ({ value: v, label: v }))} />
          </Form.Item>
          <Form.Item name="site_reference" label="Site reference">
            <Input placeholder="Free-text site ref (no invent pmv_sites)" />
          </Form.Item>
          <Form.Item name="load_expected" label="Load expected fleet" initialValue={true}>
            <Select
              options={[
                { value: true, label: 'Yes — from active allocations' },
                { value: false, label: 'No — empty sheet' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </WorkspaceLayout>
  );
};

export default PmvDailyLogSheetListPage;
